import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import {
  AddressLookupPrepop,
  CustomerType,
  IndustryType,
} from '@digital-platform/rs-addresses/domain';
import {
  AddressLookupPrepopService,
  AddressLookupService,
} from '@digital-platform/rs-addresses/services';
import {
  AddressLookup,
  CtaButton,
  AddressRaw,
  ProductTypeCode,
  ProductCategory,
} from '@digital-platform/rs-cms/domain';
import { AddressLookupComponent } from '../address-lookup/address-lookup.component';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';

export interface ServiceContextObj {
  customerType: CustomerType;
  industryType?: IndustryType;
  serviceAddress: AddressRaw;
  url: string;
  categoryCodes?: Array<ProductCategory>;
  productCode?: ProductTypeCode;
  showProductAvailability?: boolean;
  dateCreated?: number;
}

@Component({
  selector: 'digital-platform-request-services',
  standalone: true,
  imports: [CommonModule, AddressLookupComponent],
  providers: [CookieService],
  templateUrl: './request-services.component.html',
  styleUrls: ['./request-services.component.scss'],
})
export class RequestServicesComponent
  extends BasePTypeComponent
  implements OnInit, AddressLookup
{
  public override type = 'requestServices' as const;

  @Input() public ctaButton: CtaButton[] | null = [];
  @Input() public beforeFormText: AddressLookup['beforeFormText'] = '';
  @Input() public industryCode: AddressLookup['industryCode'] = null;
  @Input() public productCategory: AddressLookup['productCategory'] | null =
    null;
  @Input() public productTypeCode: AddressLookup['productTypeCode'] | null =
    null;
  @Input() public reqServicesType: AddressLookup['reqServicesType'] | null =
    null;
  @Input() public richTextHeading: AddressLookup['richTextHeading'] | null =
    null;
  @Input()
  public showBusinessTypeSelect: AddressLookup['showBusinessTypeSelect'] = false;
  @Input()
  public showProductAvailability: AddressLookup['showProductAvailability'] = false;
  @Input() public stacked: AddressLookup['stacked'] = false;

  public storedAddress?: AddressRaw;
  public addressLookupPrepop?: AddressLookupPrepop;

  private location: Location = inject(Location);
  private router: Router = inject(Router);
  private cookieService: CookieService = inject(CookieService);
  private addressLookUpService: AddressLookupService =
    inject(AddressLookupService);
  private addressLookupPrepopServices: AddressLookupPrepopService = inject(
    AddressLookupPrepopService
  );

  public customerType: CustomerType = CustomerType.RESIDENTIAL;
  public savedCustomerType?: CustomerType;
  public businessType?: IndustryType;
  public savedBusinessType?: IndustryType;
  private address?: AddressRaw;
  private serviceContextObj?: ServiceContextObj;
  private productCategories?: Array<ProductCategory>;

  public ngOnInit(): void {
    if (this.reqServicesType?.serviceType === 'show industry') {
      this.customerType = CustomerType.COMMERCIAL;
    }

    if (this.productCategory) {
      this.productCategories = Array.isArray(this.productCategory)
        ? this.productCategory
        : [this.productCategory];
    } else {
      this.productCategories = [];
    }

    this.storedAddress = this.addressLookUpService.loadStoredAddress();
    this.addressLookupPrepopServices.addressLookupPrepop$.subscribe(
      (addressLookupPrepop: AddressLookupPrepop) => {
        this.addressLookupPrepop = addressLookupPrepop;
      }
    );
  }

  public onAddressSelect($event: {
    address?: AddressRaw;
    searchFailed?: boolean;
  }): void {
    if (!$event.searchFailed && $event.address) {
      this.address = $event.address;
      this.updateServiceContextObj();
    }
  }

  public onBusinessTypeSelect($event: IndustryType | undefined): void {
    this.businessType = $event;
    this.updateServiceContextObj();
  }

  public onCustomerTypeSelect($event: CustomerType): void {
    this.customerType = $event;
    this.updateServiceContextObj();
  }

  private updateServiceContextObj(): void {
    if (this.address) {
      this.serviceContextObj = {
        customerType: this.customerType,
        industryType: this.businessType as IndustryType,
        serviceAddress: this.address,
        url: this.location.path(),
        productCode: this.productTypeCode,
        categoryCodes: this.productCategories,
        showProductAvailability: this.showProductAvailability,
      };
    }
  }

  private storeInLocalStorage(serviceContextObj?: ServiceContextObj): void {
    if (serviceContextObj) {
      serviceContextObj.dateCreated = Date.now();
      localStorage.setItem('rsShopGate', JSON.stringify(serviceContextObj));
    }
  }

  public onSubmitButtonClicked() {
    if (
      !this.serviceContextObj ||
      (this.customerType === CustomerType.COMMERCIAL &&
        !this.serviceContextObj?.industryType)
    ) {
      return;
    }

    this.storeInLocalStorage(this.serviceContextObj);
    this.router.navigate(['shop', 'categories', { tempId: Date.now() }]);
  }
}
